import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Send, Loader2, AlertTriangle, X, Undo2, Check, MessageSquareText } from 'lucide-react'
import { BrandBar } from '../components/BrandBar'
import { PhotoSurface } from '../components/PhotoSurface'
import { useManifest } from '../state/manifest'
import { clientPolicyForManifest } from '../state/capabilities'
import { selectCounts, useSession } from '../state/session'
import { effectiveReviewTarget } from '../utils/reviewTarget'
import { useKeyboard } from '../hooks/useKeyboard'
import type { Bucket, Photo, Pick } from '../types'
import { photographerShortName } from '../utils/photographerName'
import { trackShareEvent } from '../api/client'

type PickFilter = 'all' | 'love' | 'maybe' | 'notes'

interface SelectionTile {
  photo: Photo
  bucket: Bucket
  note?: string
}

const UNDO_TTL_MS = 5000

interface RemovedPick {
  pick: Pick
  bucketLabel: string
  expiresAt: number
}

function bucketLabel(bucket: Bucket): string {
  return bucket === 'love' ? 'Selected' : bucket === 'maybe' ? 'Undecided' : 'Rejected'
}

// Best-effort haptic feedback on supported browsers. iOS Safari ignores
// vibrate, so this is a no-op there — but it lights up Android Chrome
// and gives a tactile confirmation that the action took effect.
function haptic(durationMs = 8): void {
  try {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(durationMs)
    }
  } catch {
    /* not supported */
  }
}

export function Review() {
  const navigate = useNavigate()
  const manifest = useManifest((s) => s.manifest)
  const picks = useSession((s) => s.picks)
  const shareToken = useSession((s) => s.shareToken)
  const finalize = useSession((s) => s.finalize)
  const voterName = useSession((s) => s.voterName)
  const setVoterName = useSession((s) => s.setVoterName)
  const setBucket = useSession((s) => s.setBucket)
  const setNote = useSession((s) => s.setNote)
  const clearBucket = useSession((s) => s.clearBucket)
  const counts = selectCounts(picks)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [removed, setRemoved] = useState<RemovedPick | null>(null)
  const undoTimerRef = useRef<number | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [filter, setFilter] = useState<PickFilter>('all')
  const canSelect = manifest ? clientPolicyForManifest(manifest).can.select : false

  useEffect(() => {
    if (manifest && !canSelect) navigate('/gallery', { replace: true })
  }, [canSelect, manifest, navigate])

  useEffect(() => {
    if (!manifest || !shareToken || !canSelect) return
    void trackShareEvent(shareToken, 'review_opened', {
      metadata: {
        loved: counts.love,
        maybe: counts.maybe,
        notes: counts.notes,
      },
    })
  }, [canSelect, counts.love, counts.maybe, counts.notes, manifest, shareToken])

  const photoById = useMemo(() => {
    const map: Record<string, Photo> = {}
    for (const p of manifest?.photos ?? []) map[p.id] = p
    return map
  }, [manifest])

  const photoIndexById = useMemo(() => {
    const map: Record<string, number> = {}
    ;(manifest?.photos ?? []).forEach((p, i) => {
      map[p.id] = i
    })
    return map
  }, [manifest])

  // Unified selections: every selected/undecided pick becomes a tile, sorted by
  // bucket (selected first) then by original gallery order so the client sees a
  // single coherent surface instead of two duplicate horizontal strips.
  const allTiles = useMemo<SelectionTile[]>(() => {
    const tiles: SelectionTile[] = []
    for (const pick of Object.values(picks)) {
      if (pick.bucket !== 'love' && pick.bucket !== 'maybe') continue
      const photo = photoById[pick.photoId]
      if (!photo) continue
      tiles.push({ photo, bucket: pick.bucket, note: pick.note })
    }
    return tiles.sort((a, b) => {
      if (a.bucket !== b.bucket) return a.bucket === 'love' ? -1 : 1
      return (photoIndexById[a.photo.id] ?? 0) - (photoIndexById[b.photo.id] ?? 0)
    })
  }, [picks, photoById, photoIndexById])

  const notedTiles = useMemo(
    () => allTiles.filter((t) => t.note && t.note.trim().length > 0),
    [allTiles]
  )

  const filteredTiles = useMemo(() => {
    if (filter === 'all') return allTiles
    if (filter === 'notes') return notedTiles
    return allTiles.filter((t) => t.bucket === filter)
  }, [filter, allTiles, notedTiles])

  // ── Open a photo in the swipe view (reconsider a pick) ─────────────
  const openPhoto = useCallback(
    (photoId: string) => {
      const idx = photoIndexById[photoId]
      if (typeof idx === 'number') navigate(`/photo/${idx}`)
    },
    [navigate, photoIndexById]
  )

  // ── Remove a pick with undo ────────────────────────────────────────
  const removePick = useCallback(
    (photoId: string) => {
      const existing = picks[photoId]
      if (!existing) return
      // Snapshot for undo, then clear.
      const snapshot: RemovedPick = {
        pick: existing,
        bucketLabel: bucketLabel(existing.bucket),
        expiresAt: Date.now() + UNDO_TTL_MS,
      }
      clearBucket(photoId)
      haptic()
      setRemoved(snapshot)
      if (undoTimerRef.current !== null) window.clearTimeout(undoTimerRef.current)
      undoTimerRef.current = window.setTimeout(() => {
        setRemoved(null)
        undoTimerRef.current = null
      }, UNDO_TTL_MS)
    },
    [picks, clearBucket]
  )

  const undoRemove = useCallback(() => {
    if (!removed) return
    setBucket(removed.pick.photoId, removed.pick.bucket)
    if (removed.pick.note) setNote(removed.pick.photoId, removed.pick.note)
    setRemoved(null)
    if (undoTimerRef.current !== null) {
      window.clearTimeout(undoTimerRef.current)
      undoTimerRef.current = null
    }
  }, [removed, setBucket, setNote])

  // Tick once a second while the snackbar is alive so the countdown ring animates.
  useEffect(() => {
    if (!removed) return
    const id = window.setInterval(() => setNow(Date.now()), 100)
    return () => window.clearInterval(id)
  }, [removed])

  // Clear timer on unmount.
  useEffect(
    () => () => {
      if (undoTimerRef.current !== null) window.clearTimeout(undoTimerRef.current)
    },
    []
  )

  const send = useCallback(async () => {
    setSending(true)
    setSendError(null)
    try {
      await finalize()
      navigate('/done')
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : "We couldn't send your picks. Check your connection and try again."
      setSendError(message)
    } finally {
      setSending(false)
    }
  }, [finalize, navigate])

  const canSend = counts.love + counts.maybe > 0

  // Keyboard: Enter sends, Escape goes back to gallery, U undoes the last removal.
  useKeyboard(
    useMemo(
      () => ({
        Enter: () => {
          if (canSend && !sending) void send()
        },
        Escape: () => navigate('/gallery'),
        u: () => {
          if (removed) undoRemove()
        },
      }),
      [canSend, sending, send, navigate, removed, undoRemove]
    )
  )

  if (!manifest) return null
  if (!canSelect) {
    return null
  }
  const target = effectiveReviewTarget(manifest.quotaTarget, manifest.photos.length)
  const inRange = !target || (counts.love >= target.min && counts.love <= target.max)
  const messageGood = !target
    ? `You've picked ${counts.love} of ${manifest.photos.length}.`
    : inRange
      ? `Looking good — you've picked ${counts.love}, right in the album range.`
      : counts.love < target.min
        ? `You've picked ${counts.love} — try to get to ${target.min} for the album.`
        : `You've picked ${counts.love} — a few over but that's fine if you can't choose.`
  const photographerName = photographerShortName(manifest.photographer.name)

  const undoMsLeft = removed ? Math.max(0, removed.expiresAt - now) : 0
  const undoPct = removed ? undoMsLeft / UNDO_TTL_MS : 0

  return (
    <div className="flex flex-col min-h-[100dvh]">
      <div className="pt-[max(2.25rem,env(safe-area-inset-top))] md:pt-3" />
      <BrandBar
        title="Review your picks"
        subtitle={`Before sending to ${photographerName}`}
        back
        brandLogoUrl={manifest.photographer.brandLogoUrl}
      />

      <div className="px-5 pt-1 pb-4 flex-shrink-0">
        <h2 className="text-2xl font-semibold tracking-tight">Your selections</h2>
        <p className="text-[13px] text-[var(--text-secondary)] mt-1">{messageGood}</p>
        <p className="hidden sm:block text-[12px] text-[var(--text-tertiary)] mt-2">
          Tap a photo to reconsider · tap × to remove · Enter to send
        </p>
      </div>

      <main className="flex-1 overflow-y-auto px-4 pb-56 md:pb-32">
        {allTiles.length > 0 && (
          <>
            <FilterChips
              active={filter}
              onChange={setFilter}
              counts={{
                all: allTiles.length,
                love: counts.love,
                maybe: counts.maybe,
                notes: notedTiles.length,
              }}
            />
            <SelectionsGrid tiles={filteredTiles} onOpen={openPhoto} onRemove={removePick} />
            {filteredTiles.length === 0 && (
              <div className="text-center text-[12px] text-[var(--text-tertiary)] py-8">
                {filter === 'notes'
                  ? 'No notes yet — leave one on any photo to flag context for the photographer.'
                  : `No ${filter} picks yet.`}
              </div>
            )}
          </>
        )}

        {notedTiles.length > 0 && (
          <section className="mt-6">
            <div className="flex items-baseline gap-2 px-1 pt-2 pb-2.5">
              <span className="flex items-center gap-2 text-[13px] font-semibold">
                <MessageSquareText
                  size={12}
                  strokeWidth={2.2}
                  className="text-[var(--text-secondary)]"
                  aria-hidden
                />
                Notes for your photographer
              </span>
              <span className="text-[12px] text-[var(--text-tertiary)]">· {notedTiles.length}</span>
            </div>
            <div className="flex flex-col gap-1.5">
              {notedTiles.map((tile) => (
                <NoteCard
                  key={`note-${tile.photo.id}`}
                  tile={tile}
                  onOpen={openPhoto}
                  onRemove={removePick}
                />
              ))}
            </div>
          </section>
        )}

        {counts.love === 0 && counts.maybe === 0 && (
          <div className="text-center py-16 text-sm text-[var(--text-tertiary)]">
            You haven't picked anything yet.
            <div className="mt-3">
              <button
                onClick={() => navigate('/gallery')}
                className="inline-flex items-center min-h-[44px] px-3 text-[var(--text-primary)] underline underline-offset-2"
              >
                Back to gallery
              </button>
            </div>
          </div>
        )}
      </main>

      <div
        className="fixed bottom-0 left-0 right-0 md:absolute bg-[var(--surface-primary)] border-t border-[var(--border-subtle)] px-4 py-4 mx-auto md:max-w-[inherit] md:w-full md:left-0 md:right-0"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        {removed && (
          <UndoSnackbar
            label={`Removed from ${removed.bucketLabel}`}
            secondsLeft={Math.ceil(undoMsLeft / 1000)}
            ringPct={undoPct}
            onUndo={undoRemove}
          />
        )}
        {sendError && (
          <div
            role="alert"
            className="mb-2.5 px-3 py-2 rounded-xl bg-[var(--accent-danger-soft,rgba(239,68,68,0.12))] border border-[var(--accent-danger,#ef4444)]/40 text-[12px] text-[var(--accent-danger,#ef4444)] flex items-start gap-2"
          >
            <AlertTriangle size={14} strokeWidth={2} className="mt-[1px] flex-shrink-0" />
            <span className="leading-snug">{sendError}</span>
          </div>
        )}
        <label className="mb-2.5 block">
          <span className="sr-only">Your name (optional)</span>
          <input
            type="text"
            value={voterName}
            onChange={(e) => setVoterName(e.target.value)}
            placeholder="Your name (optional) — so the photographer knows who's reviewing"
            autoComplete="name"
            maxLength={80}
            data-testid="review-voter-name"
            className="h-11 w-full rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-secondary)] px-3 text-[13px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--text-tertiary)]"
          />
        </label>
        <button
          onClick={send}
          disabled={!canSend || sending}
          className="w-full rounded-2xl bg-[var(--accent-primary)] text-[#0a0a0a] text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ height: '52px' }}
        >
          {sending ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Sending…
            </>
          ) : (
            <>
              Send picks
              <Send size={16} strokeWidth={2.2} />
            </>
          )}
        </button>
        <div className="text-[12px] text-[var(--text-tertiary)] text-center mt-2">
          Your photographer sees your picks land in ShotSelect immediately
        </div>
      </div>
    </div>
  )
}

/**
 * Pill row that scopes the unified selections grid by bucket / by notes.
 * Live count beside each chip mirrors what's currently rendered below,
 * so the client never has to guess what "Undecided" means.
 */
function FilterChips({
  active,
  onChange,
  counts,
}: {
  active: PickFilter
  onChange: (next: PickFilter) => void
  counts: { all: number; love: number; maybe: number; notes: number }
}) {
  const chips: { key: PickFilter; label: string; count: number; dot?: string }[] = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'love', label: 'Selected', count: counts.love, dot: 'var(--accent-success)' },
    { key: 'maybe', label: 'Undecided', count: counts.maybe, dot: 'var(--text-tertiary)' },
    { key: 'notes', label: 'Notes', count: counts.notes, dot: 'var(--accent-primary)' },
  ]
  return (
    <div
      role="tablist"
      aria-label="Filter selections"
      className="flex gap-1.5 overflow-x-auto no-scrollbar px-1 pt-2 pb-3 -mx-1"
    >
      {chips.map((chip) => {
        const isActive = chip.key === active
        const disabled = chip.count === 0 && chip.key !== 'all'
        return (
          <button
            key={chip.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={disabled}
            onClick={() => onChange(chip.key)}
            className={[
              'inline-flex items-center gap-1.5 h-11 px-4 rounded-full text-[12px] font-medium border whitespace-nowrap transition-colors sm:h-8 sm:px-3',
              isActive
                ? 'bg-[var(--surface-quaternary)] border-[var(--border-strong)] text-[var(--text-primary)]'
                : 'bg-transparent border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
              disabled && 'opacity-40 cursor-not-allowed',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {chip.dot && (
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: chip.dot }}
                aria-hidden
              />
            )}
            {chip.label}
            <span className="text-[var(--text-tertiary)] tabular-nums">{chip.count}</span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * Single unified grid of every selected/undecided pick. The bucket is communicated
 * tile-by-tile rather than by section heading: selected tiles get a soft green
 * ring + ✓ pip, undecided tiles get a neutral ring + dimming + M pip. Photos
 * with notes carry an extra speech-bubble pip on the bottom-left so the
 * bride can spot them at a glance.
 */
function SelectionsGrid({
  tiles,
  onOpen,
  onRemove,
}: {
  tiles: SelectionTile[]
  onOpen: (photoId: string) => void
  onRemove: (photoId: string) => void
}) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-1.5 auto-rows-[112px] sm:auto-rows-[120px] md:auto-rows-[130px] lg:auto-rows-[140px]">
      {tiles.map((tile) => (
        <SelectionTileView key={tile.photo.id} tile={tile} onOpen={onOpen} onRemove={onRemove} />
      ))}
    </div>
  )
}

function SelectionTileView({
  tile,
  onOpen,
  onRemove,
}: {
  tile: SelectionTile
  onOpen: (photoId: string) => void
  onRemove: (photoId: string) => void
}) {
  const { photo, bucket, note } = tile
  const isLoved = bucket === 'love'
  const hasNote = Boolean(note && note.trim().length > 0)
  return (
    <div className="relative group">
      <button
        type="button"
        onClick={() => onOpen(photo.id)}
        aria-label={`${bucketLabel(bucket)} · open photo to reconsider${hasNote ? ' (has note)' : ''}`}
        className={[
          'absolute inset-0 rounded-xl overflow-hidden active:scale-[0.98] transition-transform ring-1',
          isLoved
            ? 'ring-[var(--accent-success-ring,rgba(34,197,94,0.5))]'
            : 'ring-[var(--border-subtle)]',
        ].join(' ')}
        style={!isLoved ? { opacity: 0.78 } : undefined}
      >
        <PhotoSurface src={photo.thumbUrl} alt="" rounded="" className="absolute inset-0" />
      </button>
      {/* Bucket pip · top-left */}
      <div
        aria-hidden
        className={[
          'absolute top-1.5 left-1.5 w-5 h-5 rounded-full grid place-items-center pointer-events-none',
          isLoved
            ? 'bg-[var(--accent-success)] text-[#052e10]'
            : 'bg-[var(--surface-quaternary)] border border-[var(--border-strong)] text-[var(--text-primary)]',
        ].join(' ')}
      >
        {isLoved ? (
          <Check size={11} strokeWidth={2.8} />
        ) : (
          <span className="text-[9px] font-bold leading-none">M</span>
        )}
      </div>
      {/* Note pip · bottom-left */}
      {hasNote && (
        <div
          aria-hidden
          title={note}
          className="absolute bottom-1.5 left-1.5 w-5 h-5 rounded-full bg-black/55 backdrop-blur-sm text-white grid place-items-center pointer-events-none"
        >
          <MessageSquareText size={10} strokeWidth={2.2} />
        </div>
      )}
      {/* Remove × · top-right (always visible on touch; reveal-on-hover desktop) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onRemove(photo.id)
        }}
        aria-label={`Remove ${bucketLabel(bucket).toLowerCase()} pick`}
        className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 text-white grid place-items-center opacity-90 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
      >
        <X size={12} strokeWidth={2.6} />
      </button>
    </div>
  )
}

/**
 * Card row used in the "Your notes" recap section beneath the unified grid.
 * Tap the card → open the photo to reconsider; tap × → remove the pick.
 */
function NoteCard({
  tile,
  onOpen,
  onRemove,
}: {
  tile: SelectionTile
  onOpen: (photoId: string) => void
  onRemove: (photoId: string) => void
}) {
  const { photo, bucket, note } = tile
  return (
    <div
      className="group relative bg-[var(--surface-secondary)] border border-[var(--border-subtle)] rounded-2xl p-3 flex gap-3 cursor-pointer transition-colors hover:bg-[var(--surface-tertiary,var(--surface-secondary))]"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(photo.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(photo.id)
        }
      }}
      aria-label={`Open ${bucketLabel(bucket)} photo with note`}
    >
      <PhotoSurface
        src={photo.thumbUrl}
        alt=""
        rounded="rounded-lg"
        className="relative w-14 h-14 flex-shrink-0"
      />
      <div className="flex-1 text-[13px] text-[var(--text-primary)] leading-relaxed pr-7">
        {note}
        <div className="text-[12px] uppercase tracking-wider text-[var(--text-tertiary)] mt-1">
          {bucketLabel(bucket)} · {photo.category}
        </div>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onRemove(photo.id)
        }}
        aria-label={`Remove ${bucketLabel(bucket).toLowerCase()} note from review`}
        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/40 hover:bg-black/60 text-white grid place-items-center opacity-70 hover:opacity-100 transition-opacity"
      >
        <X size={14} strokeWidth={2.4} />
      </button>
    </div>
  )
}

function UndoSnackbar({
  label,
  secondsLeft,
  ringPct,
  onUndo,
}: {
  label: string
  secondsLeft: number
  ringPct: number
  onUndo: () => void
}) {
  // SVG ring countdown — 18px circle, 2px stroke, animates from full to empty.
  const RADIUS = 8
  const CIRC = 2 * Math.PI * RADIUS
  const offset = CIRC * (1 - ringPct)

  return (
    <div
      role="status"
      aria-live="polite"
      className="mb-2.5 px-3 py-2 rounded-xl bg-[var(--surface-tertiary,#262626)] border border-[var(--border-subtle)] text-[12px] text-[var(--text-primary)] flex items-center gap-3"
    >
      <svg width="18" height="18" viewBox="0 0 18 18" className="flex-shrink-0" aria-hidden>
        <circle
          cx="9"
          cy="9"
          r={RADIUS}
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="2"
        />
        <circle
          cx="9"
          cy="9"
          r={RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeDasharray={CIRC}
          strokeDashoffset={offset}
          transform="rotate(-90 9 9)"
          style={{ transition: 'stroke-dashoffset 100ms linear' }}
        />
      </svg>
      <span className="flex-1 leading-snug">
        {label}
        <span className="text-[var(--text-tertiary)]"> · {secondsLeft}s</span>
      </span>
      <button
        type="button"
        onClick={onUndo}
        className="inline-flex items-center gap-1 px-2.5 h-7 rounded-lg bg-[var(--surface-secondary)] border border-[var(--border-default)] text-[12px] font-semibold text-[var(--text-primary)] hover:bg-[var(--surface-quaternary,var(--surface-secondary))]"
        aria-label="Undo removal"
      >
        <Undo2 size={12} strokeWidth={2.4} />
        Undo
      </button>
    </div>
  )
}
